import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRestClient } from "@/lib/restClient";
import type { AccountPreferencesUpdate } from "@/lib/accountPreferences";
import type { Organization } from "./userAccessModel";

export interface ProfileAccount {
  account_id: string;
  email: string;
  sub?: string;
  configured_profile_keys: string[] | null;
  configured_scope_keys: string[] | null;
  profile_origin?: string;
  scope_origin?: string;
  roles?: string[];
  profile: string;
  scopes: string[];
  settings?: Record<string, unknown>;
  data_limited_to_scopes?: boolean;
  last_seen_at?: string | null;
  created_at?: string;
  updated_at?: string;
  access_diagnostics?: { unknown_profile_keys?: string[] };
}

export function useAdminAccounts(enabled = true) {
  const apiFetch = useRestClient();
  return useQuery({
    queryKey: ["accounts", "profiles"],
    queryFn: () => apiFetch<ProfileAccount[]>("/api/accounts"),
    enabled,
  });
}

export function useAccessCatalog(enabled = true) {
  const apiFetch = useRestClient();
  return useQuery({
    queryKey: ["access-scopes"],
    queryFn: () => apiFetch<{ organizations: Organization[] }>("/api/admin/access-scopes"),
    enabled,
  });
}

export interface AccessChange {
  accountId: string;
  profile_keys: string[] | null;
  scope_keys: string[] | null;
}

/**
 * Account administration: a profile and scopes in one request, personal
 * preferences, and the same change applied to several accounts at once.
 */
export function useAccountAccessAdmin() {
  const apiFetch = useRestClient();
  const client = useQueryClient();
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["accounts"] }),
      client.invalidateQueries({ queryKey: ["account"] }),
      client.invalidateQueries({ queryKey: ["tenants"] }),
    ]);
  const putAccess = ({ accountId, ...body }: AccessChange) =>
    apiFetch<ProfileAccount>(`/api/accounts/${encodeURIComponent(accountId)}/identity-overrides`, {
      method: "PUT",
      body: JSON.stringify(body),
    });

  const access = useMutation({ mutationFn: putAccess, onSuccess: refresh });

  const preferences = useMutation({
    mutationFn: ({
      accountId,
      changes,
    }: {
      accountId: string;
      changes: AccountPreferencesUpdate;
    }) =>
      apiFetch(`/api/accounts/${encodeURIComponent(accountId)}/preferences`, {
        method: "PUT",
        body: JSON.stringify(changes),
      }),
    onSuccess: refresh,
  });

  /** Applies every change; reports how many failed instead of stopping at the first. */
  const bulkAccess = useMutation({
    mutationFn: async (changes: AccessChange[]) => {
      const results = await Promise.allSettled(changes.map(putAccess));
      const failed = results.filter((result) => result.status === "rejected");
      return { updated: results.length - failed.length, failed: failed.length };
    },
    onSettled: refresh,
  });

  return { access, preferences, bulkAccess };
}
